import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../../i18n/I18nContext";
import { api } from "../../lib/api";
import { motion } from "framer-motion";
import { Camera, CalendarCheck, MapPin, Star, ArrowLeft, ArrowRight, Sparkles, Check, Quote } from "lucide-react";

const rise = { initial: { opacity: 0, y: 26 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.55 } };

export default function Home() {
  const { t, isAr, fmtSAR, localize } = useI18n();
  const [content, setContent] = useState({});
  const [services, setServices] = useState([]);
  const [gallery, setGallery] = useState([]);
  const Arrow = isAr ? ArrowLeft : ArrowRight;

  useEffect(() => {
    api.get("/content").then((r) => setContent(r.data || {})).catch(() => {});
    api.get("/services").then((r) => setServices(r.data.slice(0, 4))).catch(() => {});
    api.get("/gallery").then((r) => setGallery(r.data.slice(0, 6))).catch(() => {});
  }, []);

  const hero = content.hero || {};
  const mk = content.marketing || {};
  const g = (i, fb) => gallery[i]?.image || fb;
  const heroImg = hero.image || "https://images.unsplash.com/photo-1706565027774-165d723eafa5?crop=entropy&cs=srgb&fm=jpg&q=85";
  const types = isAr ? ["بورتريه", "أعراس", "منتجات", "عائلي", "تخرّج", "أطفال حديثي الولادة", "علامات تجارية", "أزياء"] : ["Portrait", "Weddings", "Products", "Family", "Graduation", "Newborn", "Branding", "Fashion"];
  const accents = ["--brand-blue", "--brand-orange", "--brand-yellow", "--brand-red"];

  return (
    <div className="overflow-hidden">
      {/* HERO — split colour block + image bento */}
      <section className="mx-auto max-w-7xl px-4 pt-8 sm:px-8 lg:pt-14">
        <div className="grid items-stretch gap-6 lg:grid-cols-2">
          <motion.div {...rise} className="relative flex flex-col justify-center overflow-hidden rounded-[2rem] p-8 text-white sm:p-12"
            style={{ backgroundImage: "linear-gradient(135deg, hsl(var(--brand-blue)) 0%, hsl(217 91% 45%) 45%, hsl(var(--brand-orange)) 120%)" }}>
            <div className="pointer-events-none absolute -end-16 -top-16 h-64 w-64 rounded-full bg-white/10 blur-2xl" />
            <div className="pointer-events-none absolute -bottom-20 -start-10 h-56 w-56 rounded-full bg-[hsl(var(--brand-yellow))]/20 blur-3xl" />
            <span className="relative inline-flex w-fit items-center gap-2 rounded-full bg-white/15 px-4 py-1.5 text-xs font-semibold backdrop-blur"><Sparkles className="h-3.5 w-3.5" /> {isAr ? hero.badge_ar : hero.badge_en || "Motawahj · Saudi Arabia"}</span>
            <h1 className="relative mt-6 text-4xl font-black leading-[1.1] sm:text-5xl lg:text-6xl">
              {isAr ? (hero.title_ar || "لحظاتك تستحق أن") : (hero.title_en || "Moments worth")}{" "}
              <span className="text-[hsl(var(--brand-yellow))]">{isAr ? "تتوهّج" : "glowing for"}</span>
            </h1>
            <p className="relative mt-4 max-w-md text-base text-white/85">{isAr ? hero.subtitle_ar : hero.subtitle_en}</p>
            <div className="relative mt-8 flex flex-wrap gap-3">
              <Link to="/booking" data-testid="hero-book-btn" className="inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-sm font-bold text-slate-900 shadow-lg transition-transform hover:scale-105 active:scale-95">{isAr ? hero.cta_primary_ar : hero.cta_primary_en || "Book Now"} <Arrow className="h-4 w-4" /></Link>
              <Link to="/services" data-testid="hero-services-btn" className="inline-flex items-center gap-2 rounded-full border border-white/40 px-7 py-3.5 text-sm font-bold text-white hover:bg-white/10">{isAr ? hero.cta_secondary_ar : hero.cta_secondary_en || "Explore"}</Link>
            </div>
            <div className="relative mt-8 flex items-center gap-5 text-sm text-white/80">
              <span className="inline-flex items-center gap-1.5"><MapPin className="h-4 w-4" /> {isAr ? "فروع متعددة" : "Multi-branch"}</span>
              <span className="inline-flex items-center gap-1.5"><Camera className="h-4 w-4" /> {isAr ? "مصورون محترفون" : "Pro photographers"}</span>
            </div>
          </motion.div>

          <motion.div {...rise} transition={{ duration: 0.6, delay: 0.1 }} className="grid grid-cols-2 gap-4">
            <div className="row-span-2 overflow-hidden rounded-[2rem]"><img src={heroImg} alt="" className="h-full w-full object-cover" /></div>
            <div className="overflow-hidden rounded-[2rem]"><img src={g(0, "https://images.unsplash.com/photo-1519741497674-611481863552?crop=entropy&cs=srgb&fm=jpg&q=85")} alt="" className="h-full w-full object-cover" /></div>
            <div className="overflow-hidden rounded-[2rem] bg-[hsl(var(--brand-orange))]/10 p-5">
              <div className="flex h-full flex-col justify-between">
                <Quote className="h-6 w-6 text-[hsl(var(--brand-orange))]" />
                <div><div className="flex text-[hsl(var(--brand-yellow))]">{[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-current" />)}</div>
                  <p className="mt-1 text-xs text-slate-500">{isAr ? "تجربة تصوير راقية" : "A premium shoot experience"}</p></div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Marquee of studio types */}
      <div className="mt-12 border-y border-border bg-white py-4">
        <div className="mx-auto flex max-w-7xl flex-wrap justify-center gap-2 px-4 sm:px-8">
          {types.map((ty, i) => (
            <span key={ty} className="rounded-full border border-border px-4 py-1.5 text-sm font-medium" style={{ color: `hsl(var(${accents[i % 4]}))` }}>{ty}</span>
          ))}
        </div>
      </div>

      {/* How it works */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-8">
        <motion.h2 {...rise} className="text-center text-3xl font-black text-slate-900 sm:text-4xl">{isAr ? "احجز في ثلاث خطوات" : "Book in three steps"}</motion.h2>
        <div className="mt-10 grid gap-5 md:grid-cols-3">
          {[[isAr ? "اختر خدمتك" : "Choose service", isAr ? "تصفّح الباقات واختر ما يناسب مناسبتك" : "Browse packages for your occasion", Camera],
            [isAr ? "حدّد الموعد والفرع" : "Pick time & branch", isAr ? "توافر فوري مع منع التعارض" : "Live availability, no clashes", CalendarCheck],
            [isAr ? "أكمل واستمتع" : "Confirm & enjoy", isAr ? "ادفع العربون واحضر لجلستك" : "Pay deposit and show up", Star]].map(([ti, de, Icon], i) => (
            <motion.div key={i} {...rise} transition={{ duration: 0.5, delay: i * 0.08 }} className="rounded-3xl border border-border bg-white p-7">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl text-white" style={{ backgroundColor: `hsl(var(${accents[i]}))` }}><Icon className="h-6 w-6" /></div>
              <div className="mt-4 text-xs font-bold text-slate-400">0{i + 1}</div>
              <h3 className="mt-1 text-lg font-bold text-slate-900">{ti}</h3>
              <p className="mt-1 text-sm text-slate-500">{de}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Services showcase — colourful bento */}
      <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-8">
        <div className="flex items-end justify-between">
          <div><h2 className="text-3xl font-black text-slate-900 sm:text-4xl">{isAr ? mk.services_title_ar : mk.services_title_en || "Our Services"}</h2>
            <p className="mt-1 text-slate-500">{isAr ? mk.services_sub_ar : mk.services_sub_en}</p></div>
          <Link to="/services" className="hidden items-center gap-1 text-sm font-bold text-primary hover:underline sm:inline-flex">{isAr ? "الكل" : "All"} <Arrow className="h-4 w-4" /></Link>
        </div>
        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {services.map((s, i) => (
            <motion.div key={s.id} {...rise} transition={{ duration: 0.5, delay: i * 0.06 }}>
              <Link to={`/services/${s.id}`} data-testid={`home-service-${s.id}`} className="group block overflow-hidden rounded-3xl border border-border bg-white transition-all hover:-translate-y-1 hover:shadow-xl">
                <div className="relative aspect-[4/5] overflow-hidden">
                  <img src={s.image || g(i + 1, heroImg)} alt="" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110" />
                  <div className="absolute inset-x-0 bottom-0 h-1.5" style={{ backgroundColor: `hsl(var(${accents[i % 4]}))` }} />
                </div>
                <div className="p-5">
                  <h3 className="font-bold text-slate-900">{localize(s, "name")}</h3>
                  <div className="mt-2 flex items-center justify-between">
                    <span className="text-xs text-slate-400">{s.duration_minutes} {isAr ? "دقيقة" : "min"}</span>
                    <span className="rounded-full px-3 py-1 text-sm font-bold text-white" style={{ backgroundColor: `hsl(var(${accents[i % 4]}))` }}>{fmtSAR(s.price)}</span>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Stats band */}
      <section className="px-4 sm:px-8">
        <div className="mx-auto max-w-7xl overflow-hidden rounded-[2.5rem] p-10 text-white sm:p-14" style={{ backgroundImage: "linear-gradient(120deg, hsl(var(--brand-blue)), hsl(var(--brand-orange)))" }}>
          <div className="grid gap-8 text-center sm:grid-cols-3">
            {[["3", isAr ? "جلسات = جلسة مجانية" : "sessions = 1 free"], ["15%", isAr ? "ضريبة شفافة" : "transparent VAT"], ["24/7", isAr ? "حجز أونلاين" : "online booking"]].map(([n, l], i) => (
              <motion.div key={i} {...rise} transition={{ duration: 0.5, delay: i * 0.1 }}>
                <div className="text-5xl font-black">{n}</div>
                <div className="mt-1 text-sm text-white/85">{l}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery mosaic */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-8">
        <h2 className="text-3xl font-black text-slate-900 sm:text-4xl">{isAr ? mk.gallery_title_ar : mk.gallery_title_en || "Our Work"}</h2>
        <div className="mt-8 grid auto-rows-[160px] grid-cols-2 gap-4 sm:grid-cols-4">
          {(gallery.length ? gallery : Array.from({ length: 6 })).slice(0, 6).map((it, i) => (
            <motion.div key={i} {...rise} transition={{ duration: 0.5, delay: i * 0.05 }} className={`overflow-hidden rounded-3xl ${i === 0 || i === 3 ? "row-span-2" : ""}`}>
              <img src={it?.image || g(i, heroImg)} alt="" className="h-full w-full object-cover transition-transform duration-500 hover:scale-105" />
            </motion.div>
          ))}
        </div>
      </section>

      {/* Testimonials (demo) */}
      <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-8">
        <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-500">{isAr ? "محتوى تجريبي للعرض" : "Demo content for showcase"}</div>
        <div className="grid gap-5 md:grid-cols-3">
          {[[isAr ? "نورة" : "Noura", isAr ? "جلسة عائلية رائعة والنتائج فاقت التوقعات." : "Wonderful family session, results beyond expectations."],
            [isAr ? "خالد" : "Khalid", isAr ? "حجز سهل ومصور محترف جداً." : "Easy booking and a very professional photographer."],
            [isAr ? "سارة" : "Sara", isAr ? "التصوير المنزلي كان مريحاً وأنيقاً." : "The home shoot was comfortable and elegant."]].map(([nm, tx], i) => (
            <motion.div key={i} {...rise} transition={{ duration: 0.5, delay: i * 0.08 }} className="rounded-3xl border border-border bg-white p-6">
              <div className="flex text-[hsl(var(--brand-yellow))]">{[...Array(5)].map((_, j) => <Star key={j} className="h-4 w-4 fill-current" />)}</div>
              <p className="mt-3 text-slate-700">"{tx}"</p>
              <div className="mt-4 flex items-center gap-2"><span className="flex h-9 w-9 items-center justify-center rounded-full text-white" style={{ backgroundColor: `hsl(var(${accents[i % 4]}))` }}>{nm[0]}</span><span className="text-sm font-semibold text-slate-900">{nm}</span></div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="px-4 pb-20 sm:px-8">
        <motion.div {...rise} className="mx-auto max-w-7xl rounded-[2.5rem] bg-slate-950 p-10 text-center sm:p-16">
          <h2 className="text-3xl font-black text-white sm:text-5xl">{isAr ? mk.cta_title_ar : mk.cta_title_en || "Ready for your next moment?"}</h2>
          <Link to="/booking" className="mt-8 inline-flex items-center gap-2 rounded-full px-9 py-4 text-base font-bold text-white shadow-xl transition-transform hover:scale-105 active:scale-95" style={{ backgroundImage: "linear-gradient(90deg, hsl(var(--brand-blue)), hsl(var(--brand-orange)))" }}>
            <CalendarCheck className="h-5 w-5" /> {t("nav.book")}
          </Link>
        </motion.div>
      </section>
    </div>
  );
}
